import { BackendApiError } from './minecardsBackendClient'

export const sessionChangedEvent = 'minecards:session-changed'

const request = async (
    path,
    {
        user,
        baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || '',
        fetchImpl = window.fetch.bind(window),
        idempotencyKey,
    } = {}
) => {
    const headers = { Accept: 'application/json' }
    if (user) {
        if (typeof user.getIdToken !== 'function') {
            throw new BackendApiError('Firebase authentication is required', {
                code: 'unauthenticated',
            })
        }
        headers.Authorization = `Bearer ${await user.getIdToken()}`
    }
    if (idempotencyKey) {
        headers['Idempotency-Key'] = idempotencyKey
    }

    let response
    try {
        response = await fetchImpl(
            `${String(baseUrl).replace(/\/+$/, '')}${path}`,
            {
                method: 'POST',
                headers,
                credentials: 'include',
            }
        )
    } catch (error) {
        throw new BackendApiError('Backend request failed', {
            code: 'network_error',
            cause: error,
        })
    }

    const rawBody = await response.text()
    let payload = null
    if (rawBody) {
        try {
            payload = JSON.parse(rawBody)
        } catch (error) {
            throw new BackendApiError('Backend returned invalid JSON', {
                status: response.status,
                code: 'invalid_response',
                cause: error,
            })
        }
    }
    if (!response.ok) {
        const apiError = payload?.error || {}
        throw new BackendApiError(
            apiError.message || `Backend request failed (${response.status})`,
            {
                status: response.status,
                code: apiError.code || 'http_error',
                requestId: apiError.requestId,
            }
        )
    }
    return payload
}

export const exchangeFirebaseSession = (user, options) =>
    request('/api/v1/auth/firebase/exchange', { ...options, user })

export const logoutMinecardsSession = (options) =>
    request('/api/v1/auth/logout', options)

const guestKeyStorageName = 'minecards:pending-guest-key'

const randomGuestKey = () => {
    if (typeof window.crypto?.randomUUID === 'function') {
        return `guest:${window.crypto.randomUUID()}`
    }
    if (typeof window.crypto?.getRandomValues !== 'function') {
        throw new BackendApiError('Secure random generator is unavailable', {
            code: 'secure_random_unavailable',
        })
    }
    const bytes = window.crypto.getRandomValues(new Uint8Array(16))
    const token = Array.from(bytes, (value) =>
        value.toString(16).padStart(2, '0')
    ).join('')
    return `guest:${token}`
}

export const createGuestSession = async (
    options = {},
    storage = window.localStorage,
    keyFactory = randomGuestKey
) => {
    let idempotencyKey = storage.getItem(guestKeyStorageName)
    if (!idempotencyKey) {
        idempotencyKey = keyFactory()
        storage.setItem(guestKeyStorageName, idempotencyKey)
    }
    const result = await request('/api/v1/auth/guest', {
        ...options,
        idempotencyKey,
    })
    storage.removeItem(guestKeyStorageName)
    return result
}

export const notifySessionChanged = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(sessionChangedEvent))
    }
}
